import { Box, Chip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { StoryScene } from "../../types/storyMap";
import { coverStaticMapUrl } from "./storyLayers";
import { sideCaptureKey } from "./print/useSceneCaptures";

/**
 * The still shown in place of a scene's live map when the story is embedded.
 *
 * Source order, cheapest first:
 *  1. the PNG captured for this scene at publish (already stored for the PDF)
 *     — costs nothing, and is exactly what the reader would have seen;
 *  2. the Mapbox Static Images API for the scene camera — a separate metered
 *     SKU, cheaper per view than a GL map load but not free, so it is only a
 *     fallback for stories published before the captures existed;
 *  3. a plain placeholder.
 */
export default function EmbedSceneImage({
  scene,
  sceneImages,
  storyUrl,
  note: extraNote = null,
  showOpenLink = true,
}: {
  scene: StoryScene | undefined;
  sceneImages: Record<string, string> | undefined;
  storyUrl: string;
  /** Extra caption, e.g. why a still is shown instead of a live map. */
  note?: string | null;
  /** The "Open the full story" link — pointless when this IS the story. */
  showOpenLink?: boolean;
}) {
  // A compare scene has no single "the map": show the treated ("after") side,
  // which is the state the story is arguing for, and point at the full story
  // for the side-by-side.
  const isCompare = scene?.mode === "compare" && !!scene.compare;
  const captureKey = scene
    ? isCompare
      ? sideCaptureKey(scene.id, "after")
      : scene.id
    : null;

  const captured = captureKey ? sceneImages?.[captureKey] : undefined;
  const fallback =
    !captured && scene?.camera?.center ? coverStaticMapUrl(scene.camera, 1280, 800) : null;
  const src = captured ?? fallback;

  const modeNote = isCompare
    ? "Showing the treated scenario"
    : scene?.mode === "animation"
    ? "Showing the final frame"
    : null;
  const note = [extraNote, modeNote].filter(Boolean).join(" · ") || null;

  return (
    <Box sx={{ position: "relative", height: "100%", bgcolor: "#0e1519" }}>
      {src ? (
        <Box
          component="img"
          src={src}
          alt={scene?.name ?? "Story map"}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.5)",
          }}
        >
          <Typography variant="body2">Map image unavailable</Typography>
        </Box>
      )}

      {note && (
        <Chip
          size="small"
          label={note}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            bgcolor: "rgba(14,21,25,0.75)",
            color: "white",
          }}
        />
      )}

      {/* The way out of a flat embed: the live, explorable story. */}
      {showOpenLink && (
        <Chip
          clickable
          component="a"
          href={storyUrl}
          target="_blank"
          rel="noopener noreferrer"
          icon={<OpenInNewIcon sx={{ color: "#00d4ad !important" }} />}
          label="Open the full story"
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            bgcolor: "rgba(33,43,55,0.9)",
            color: "white",
            "&:hover": { bgcolor: "rgba(33,43,55,1)" },
          }}
        />
      )}
    </Box>
  );
}
